<template>
  <div class="p-company-settings-settings">
    <VMessage
      type="warning"
      :fullwidth="true"
      v-if="!permissionCompany">
      <template v-slot:icon>
        <ExclamationTriangleIcon />
      </template>
      <template v-slot:text>
        {{ $t('companySettings.preferences.permissionWarning') }}
      </template>
    </VMessage>
    <div v-if="permissionCompany && !loading">
      <h3>{{ $t('companySettings.preferences.title') }}</h3>
      <p>{{ $t('companySettings.preferences.synopsis') }}<br><br></p>
      <VForm
        v-if="companySettings"
        data-vv-scope="company-settings">
          <div slot="row">
            <h4>{{ $t('companySettings.preferences.envelopeSettings') }}</h4>
            <hr />
          </div>
          <div slot="row">
            <VGrid>
              <div slot="gridItem" type="8">
                <div slot="row">
                  <VGrid>
                    <div slot="gridItem" type="6">
                      <h4>{{ $t('companySettings.preferences.defaultSigningSequence') }}</h4>
                    </div>
                    <!-- Default Signing Sequence -->
                    <div slot="gridItem" type="4">
                      <ElSelect @change="updatedDefaultSigningSequence" v-model="defaultSigningSequence">
                        <ElOption
                          v-for="option in defaultSigningSequenceOptions"
                          data-ec="preferences"
                          data-et="default_signing_sequence_option_dropdown"
                          :key="option.val"
                          :label="option.label"
                          :value="option.val"
                        />
                      </ElSelect>
                    </div>
                  </VGrid>
                </div>
              </div>
              <div slot="gridItem" type="4">
                <p>{{ $t('companySettings.preferences.defaultSigningSequenceExplainer') }}</p>
              </div>
            </VGrid>
          </div>
          <div slot="row">
            <VGrid>
              <div slot="gridItem" type="8">
                <div slot="row">
                  <VGrid>
                    <div slot="gridItem" type="6">
                      <h4>{{ $t('companySettings.preferences.defaultReminder') }}</h4>
                    </div>
                    <!-- Default Reminder -->
                    <div slot="gridItem" type="6">
                      <div class="flex items-center whitespace-nowrap">
                        <VCheckbox
                          id="defaultReminderNone"
                          type="radio"
                          name="reminder-interval"
                          v-model="defaultReminderNone"
                          @input="updateDefaultReminder"
                          :checked="parsedDefaultReminderNone"
                          radio-value="true"
                          class="flex-shrink-0"
                          data-ec="preferences"
                          data-et="default_reminder_none"
                        />
                        <label
                          for="defaultReminderNone"
                          class="ml-sm cursor-pointer font-bold"
                        >
                          {{ $t('companySettings.preferences.defaultReminderNone') }}
                        </label>
                      </div>
                      <div class="flex flex-wrap items-center mt-xs">
                        <label
                          for="defaultReminderCustom"
                          class="flex items-center my-xs cursor-pointer whitespace-nowrap font-bold"
                        >
                          <VCheckbox
                            id="defaultReminderCustom"
                            type="radio"
                            name="reminder-interval"
                            v-model="defaultReminderNone"
                            @input="updateDefaultReminder"
                            :checked="!parsedDefaultReminderNone"
                            radio-value="false"
                            class="flex-shrink-0"
                            data-ec="preferences"
                            data-et="default_reminder_custom"
                          />
                          <span class="ml-sm">{{ $t('companySettings.preferences.defaultReminderCustom') }}</span>
                        </label>
                        &nbsp;
                        <ElInput
                          :value="defaultReminderCustom"
                          @focus="handleDefaultReminderCustomFocus"
                          @input="handleDefaultReminderCustomInput"
                          @change="handleDefaultReminderCustomChange"
                          :aria-label="$t('companySettings.preferences.defaultReminder')"
                          class="w-14 flex-shrink-0"
                          data-ec="preferences"
                          data-et="default_reminder_input"
                        />
                        <span>&nbsp;{{ $t('companySettings.preferences.defaultReminderDays') }}</span>
                      </div>
                    </div>
                  </VGrid>
                </div>
              </div>
              <div slot="gridItem" type="4">
                <p>{{ $t('companySettings.preferences.defaultReminderExplainer') }}</p>
              </div>
            </VGrid>
          </div>
        <div slot="row">
          <h4>{{ $t('companySettings.preferences.signatureSettings') }}</h4>
          <hr />
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <div slot="row">
                <VGrid>
                  <div slot="gridItem" type="6">
                    <h4>{{ $t('companySettings.preferences.signatureInformation') }}</h4>
                  </div>
<!--                  Signature Information Checkbox-->
                  <div slot="gridItem" type="4">
                    <VCheckbox
                      type="checkbox"
                      subtype="toggle"
                      data-ec="preferences"
                      data-et="signature_information_checkbox"
                      @input="updateSettings"
                      v-model="companySettings.signature_more_info"
                      :checked="companySettings.signature_more_info" />
                    <span class="checkbox-status-indicator" >{{ companySettings.signature_more_info ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                  </div>
                </VGrid>
              </div>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.signatureInformationExplainer') }}</p>
              <img src="@/assets/company-settings/signature-info-grey.png" alt="Joan Smith signature" class="p-company-settings-settings__signature-example">
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.enableSpecificSignatureFormats') }}</h4>
                </div>
<!--                Specific Signature Formats Checkboxes-->
                <div slot="gridItem" type="4">
                  <ElCheckboxGroup
                    v-model="signatureFormatsSelected"
                    :min="1"
                    :max="3">
                    <ElCheckbox
                        data-ec="preferences"
                        :data-et="`signature_formats_checkboxes_${option.val}`"
                        v-for="option in defaultSignatureOptionMenu"
                        :label="option.val"
                        :key="option.val"
                        @change="updateSettings"
                        :disabled="companySettings.signature_format_default === option.val">
                      {{option.label}}
                    </ElCheckbox>
                  </ElCheckboxGroup>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.enableSpecificSignatureFormatsExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.defaultSignatureOption') }}</h4>
                </div>
<!--                Default Signature Option Dropdown-->
                <div slot="gridItem" type="4">
                  <ElSelect @change="updatedDefaultSignature" v-model="companySettings.signature_format_default">
                    <ElOption
                      v-for="option in defaultSignatureOptionMenu"
                      data-ec="preferences"
                      data-et="default_signature_option_dropdown"
                      :key="option.val"
                      :label="option.label"
                      :value="option.val"
                    />
                  </ElSelect>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.defaultSignatureOptionExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <h4>{{ $t('companySettings.preferences.emailSettings') }}</h4>
          <hr />
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <div slot="row">
                <VGrid>
                  <div slot="gridItem" type="6">
                    <h4>{{ $t('companySettings.preferences.pdfAttachment') }}</h4>
                  </div>
<!--                  PDF Attachment Toggle-->
                  <div slot="gridItem" type="4">
                    <VCheckbox
                      type="checkbox"
                      subtype="toggle"
                      data-ec="preferences"
                      data-et="pdf_attachment_toggle"
                      v-model="companySettings.email_pdf_attach"
                      :checked="companySettings.email_pdf_attach"
                      @input="updateSettings"
                    />
                    <span class="checkbox-status-indicator" >{{ companySettings.email_pdf_attach ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                  </div>
                </VGrid>
              </div>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.pdfAttachmentExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.envelopeOpenEmailNotifications') }}</h4>
                </div>
<!--                Envelope Open Email Notifications Toggle-->
                <div slot="gridItem" type="4">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="envelope_open_email_notifications_toggle"
                    v-model="companySettings.email_envelope_open"
                    :checked="companySettings.email_envelope_open"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.email_envelope_open ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.envelopeOpenEmailNotificationsExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.sendEmailNotifications') }}</h4>
                </div>
<!--                Send Emails When Envelopes Signed To Company Toggle-->
                <div slot="gridItem" type="4">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="send_email_when_envelope_signed_toggle"
                    v-model="companySettings.email_main_email"
                    :checked="companySettings.email_main_email"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.email_main_email ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.sendEmailNotificationsExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.envelopesReturnEmailAddress') }}</h4>
                </div>
<!--                Envelopes Return Email Address Toggle-->
                <div slot="gridItem" type="6">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="envelopes_return_email_address_toggle"
                    v-model="companySettings.email_return_address_user"
                    :checked="companySettings.email_return_address_user"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.email_return_address_user ? $t('companySettings.preferences.userEmail') : $t('companySettings.preferences.companyEmail') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.envelopesReturnEmailAddressExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <!-- Embedded signing emails -->
        <div slot="row" v-if="companySettings.embedded_signing">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.signerNotificationEmails') }}</h4>
                </div>
                <!-- Embedded signing emails toggle-->
                <div slot="gridItem" type="6">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="embedded_signing_emails"
                    v-model="companySettings.company_embedded_signing_emails_enabled"
                    :checked="companySettings.company_embedded_signing_emails_enabled"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.company_embedded_signing_emails_enabled ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <i18n path="companySettings.preferences.signerNotificationEmailsExplainer1" tag="p">
                <template v-slot:link>
                  <a href="https://documenter.getpostman.com/view/2304617/UVJYLKjV#a4ea0032-7339-4a73-9d96-00c860fce95c" target="_blank">{{ $t('companySettings.preferences.learnMore')}}</a>
                </template>
              </i18n>
              <p>{{ $t('companySettings.preferences.signerNotificationEmailsExplainer2') }}</p>
              <p>{{ $t('companySettings.preferences.signerNotificationEmailsExplainer3') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <h4>{{ $t('companySettings.preferences.signingPageSettings') }}</h4>
          <hr />
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.consumerRegulationsActCompliant') }}</h4>
                </div>
<!--                Consumer Regulations Act Compliant Toggle-->
                <div slot="gridItem" type="4">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="consumer_regulations_act_compliant_toggle"
                    v-model="companySettings.signing_consumer_regulations_act"
                    :checked="companySettings.signing_consumer_regulations_act"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.signing_consumer_regulations_act ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <i18n path="companySettings.preferences.consumerRegulationsActCompliantExplainer" tag="p" for="companySettings.preferences.legislationLink">
                <a href='http://www.legislation.gov.uk/uksi/2013/3134/pdfs/uksi_20133134_en.pdf'>{{ $t('companySettings.preferences.legislationLink') }}</a>
              </i18n>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.signingPageQuestions') }}</h4>
                </div>
<!--                Signing Page Questions Toggle-->
                <div slot="gridItem" type="4">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="signing_page_questions_toggle"
                    v-model="companySettings.signing_questions"
                    :checked="companySettings.signing_questions"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.signing_questions ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.signingPageQuestionsExplainer') }}</p>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <h4>{{ $t('companySettings.preferences.pdfCertificateSettings') }}</h4>
          <hr />
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <div slot="gridItem" type="6">
                  <h4>{{ $t('companySettings.preferences.hideEmailAddressOnPDFCertificate') }}</h4>
                </div>
<!--                Audit Trail Hide Toggle-->
                <div slot="gridItem" type="4">
                  <VCheckbox
                    type="checkbox"
                    subtype="toggle"
                    data-ec="preferences"
                    data-et="audit_trail_hide_toggle"
                    v-model="companySettings.pdf_audit_trail_hide"
                    :checked="companySettings.pdf_audit_trail_hide"
                    @input="updateSettings"
                  />
                  <span class="checkbox-status-indicator" >{{ companySettings.pdf_audit_trail_hide ? $t('functional.enabled') : $t('functional.disabled') }}</span>
                </div>
              </VGrid>
            </div>
            <div slot="gridItem" type="4">
              <p>{{ $t('companySettings.preferences.hideEmailAddressOnPDFCertificateExplainer') }}</p>
            </div>
          </VGrid>
        </div>

        <div slot="row">
          <h4>{{ $t('companySettings.preferences.sharingSettings') }}</h4>
          <hr />
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="8">
              <VGrid>
                <h4 slot="gridItem" type="6">{{ $t('companySettings.preferences.shareTypeDefault') }}</h4>
<!--                Default share public/private dropdown-->
                <div slot="gridItem" type="4">
                  <ElSelect
                    v-model="companySettings.sharing_type_default"
                    data-ec="preferences"
                    data-et="share_type_default_dropdown"
                    @change="updateSettings"
                  >
                    <ElOption
                      value="private"
                      :label="$t('companySettings.preferences.sharePrivate')"
                    />
                    <ElOption
                      value="everyone"
                      :label="$t('companySettings.preferences.shareEveryone')"
                    />
                  </ElSelect>
                </div>
              </VGrid>
            </div>
            <p slot="gridItem" type="4">{{ $t('companySettings.preferences.shareTypeDefaultExplainer') }}</p>
          </VGrid>
        </div>
      </VForm>
    </div>
    <VLoadingSpinner :overlay="false" style="margin-top: 96px;" v-if="permissionCompany && loading" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'PreferencesPage',
  inject: {
    $validator: '$validator',
  },
  components: {
    VCheckbox,
    VMessage,
    VLoadingSpinner,
    VForm,
    VGrid,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      loading: true,
      defaultSigningSequence: 'sequential',
      defaultSigningSequenceOptions: [
          { val: 'parallel', label: this.$t('companySettings.preferences.defaultSigningSequenceOptions.parallel') },
          { val: 'sequential', label: this.$t('companySettings.preferences.defaultSigningSequenceOptions.sequential') },
      ],
      defaultSignatureOptionMenu: [
        { val: 'drawn', label: this.$t('companySettings.preferences.defaultSignatureOptionMenu.drawn') },
        { val: 'typed', label: this.$t('companySettings.preferences.defaultSignatureOptionMenu.typed') },
        { val: 'upload', label: this.$t('companySettings.preferences.defaultSignatureOptionMenu.uploaded') }, // Needs to be 'upload' not 'uploaded'
      ],
      signatureFormats: ['drawn', 'typed', 'upload'],
      defaultReminderNone: true,
      defaultReminderCustom: 0,
    };
  },
  created() {
    this.$store.dispatch('settings/GET')
      .then(() => {
        this.setDefaultSigningSequence();
        this.setDefaultReminder();
      })
      .finally(() => {
        this.loading = false;
      });
  },
  computed: {
    ...mapGetters({
      companySettings: 'settings/settings',
      userHasRole: 'roles/userHasRole',
    }),
    permissionCompany() {
      return this.userHasRole('super-admin');
    },
    signatureFormatsSelected: {
      get() {
        let resp = [];
        if (this.companySettings.signature_format_typed) resp = [...resp, 'typed'];
        if (this.companySettings.signature_format_drawn) resp = [...resp, 'drawn'];
        if (this.companySettings.signature_format_upload) resp = [...resp, 'upload'];
        return resp;
      },
      set(selectedOptions) {
        this.companySettings.signature_format_typed = selectedOptions.includes('typed');
        this.companySettings.signature_format_drawn = selectedOptions.includes('drawn');
        this.companySettings.signature_format_upload = selectedOptions.includes('upload');
      },
    },
    parsedDefaultReminderNone() {
      return this.defaultReminderNone === 'true' || this.defaultReminderNone === true;
    },
  },
  methods: {
    updatedDefaultSigningSequence() {
      this.companySettings.is_parallel_signing_default = this.defaultSigningSequence === 'parallel';
      this.updateSettings();
    },
    setDefaultSigningSequence() {
      this.defaultSigningSequence = this.companySettings.is_parallel_signing_default ? 'parallel' : 'sequential';
    },
    setDefaultReminder() {
      this.defaultReminderNone = this.companySettings.auto_reminder_default_hours === 0;
      this.defaultReminderCustom = this.companySettings.auto_reminder_default_hours / 24 || this.defaultReminderCustom;
    },
    handleDefaultReminderCustomInput(event) {
      this.defaultReminderCustom = parseInt(event.slice(0, 2), 10) || '';
    },
    handleDefaultReminderCustomChange() {
      if (!this.defaultReminderCustom) {
        this.defaultReminderNone = true;
        this.defaultReminderCustom = 0;
      }
      this.updateDefaultReminder();
    },
    handleDefaultReminderCustomFocus() {
      if (this.parsedDefaultReminderNone) {
        this.defaultReminderNone = false;
        this.updateDefaultReminder();
      }
    },
    updateDefaultReminder() {
      this.companySettings.auto_reminder_default_hours = this.parsedDefaultReminderNone ? 0 : this.defaultReminderCustom * 24;
      this.updateSettings();
    },
    // Set the default type to be enabled
    updatedDefaultSignature(val) {
      this.companySettings[`signature_format_${val}`] = true;
      this.updateSettings();
    },
    updateSettings() {
      this.$store.dispatch('settings/UPDATE', this.companySettings).finally(() => {
        this.setDefaultSigningSequence();
      });
    },
  },
};
</script>

<style scoped>
.checkbox-status-indicator {
  vertical-align: top;
  line-height: var(--spacing-large);
  margin-left: var(--spacing-base);
  font-weight: bold;
}

.el-checkbox-group{
  font-size: 23px;
}

.el-checkbox__label {
  color: #000;
}

.el-checkbox__input.is-checked + .el-checkbox__label{
  color: #000;
}

.p-company-settings-settings__signature-example {
  max-width: 250px;
  margin: 0 auto;
}
</style>
